import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Edit, Trash2, Star, Tag, Palette } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useRole } from "@/hooks/useRole";
import { toast } from "sonner";

interface CourseTag {
  id: string;
  name: string;
  description: string | null;
  color: string | null;
  is_featured: boolean | null;
  created_at: string | null;
}

const PRESET_COLORS = [
  '#3B82F6', // Blue
  '#10B981', // Green  
  '#F59E0B', // Yellow
  '#EF4444', // Red
  '#8B5CF6', // Purple
  '#F97316', // Orange
  '#06B6D4', // Cyan
  '#84CC16', // Lime
  '#EC4899', // Pink
  '#6B7280'  // Gray
];

export default function TagManagement() {
  const { user } = useAuth();
  const { isAdmin } = useRole();
  const [tags, setTags] = useState<CourseTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingTag, setEditingTag] = useState<CourseTag | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#3B82F6',
    is_featured: false
  });

  useEffect(() => {
    fetchTags();
  }, []);

  const fetchTags = async () => {
    try {
      const { data, error } = await supabase
        .from('course_tags')
        .select('*')
        .order('is_featured', { ascending: false })
        .order('name');

      if (error) throw error;
      setTags(data || []);
    } catch (error) {
      console.error('Error fetching tags:', error);
      toast.error('Failed to load tags');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Tag name is required');
      return;
    }

    try {
      if (editingTag) {
        // Update existing tag
        const { error } = await supabase
          .from('course_tags')
          .update({
            name: formData.name.trim(),
            description: formData.description.trim() || null,
            color: formData.color,
            is_featured: formData.is_featured
          })
          .eq('id', editingTag.id);

        if (error) throw error;
        toast.success('Tag updated successfully');
      } else {
        // Create new tag
        const { error } = await supabase
          .from('course_tags')
          .insert({
            name: formData.name.trim(),
            description: formData.description.trim() || null,
            color: formData.color,
            is_featured: formData.is_featured
          });

        if (error) throw error;
        toast.success('Tag created successfully');
      }

      setShowCreateDialog(false);
      setEditingTag(null);
      setFormData({ name: '', description: '', color: '#3B82F6', is_featured: false });
      fetchTags();
    } catch (error) {
      console.error('Error saving tag:', error);
      toast.error('Failed to save tag');
    }
  };

  const handleEdit = (tag: CourseTag) => {
    setEditingTag(tag);
    setFormData({
      name: tag.name,
      description: tag.description || '',
      color: tag.color || '#3B82F6',
      is_featured: tag.is_featured || false
    });
    setShowCreateDialog(true);
  };

  const handleDelete = async (tagId: string) => {
    if (!confirm('Are you sure you want to delete this tag? This action cannot be undone.')) {
      return;
    }

    // Only admins can delete tags
    if (!isAdmin) {
      toast.error('Only administrators can delete tags.');
      return;
    }

    try {
      // First check if tag is being used by any courses
      const { data: mappings } = await supabase
        .from('course_tag_mappings')
        .select('id')
        .eq('tag_id', tagId)
        .limit(1);

      if (mappings && mappings.length > 0) {
        toast.error('Cannot delete tag that is assigned to courses. Remove from courses first.');
        return;
      }

      const { error } = await supabase
        .from('course_tags')
        .delete()
        .eq('id', tagId);

      if (error) throw error;
      toast.success('Tag deleted successfully');
      fetchTags();
    } catch (error) {
      console.error('Error deleting tag:', error);
      toast.error('Failed to delete tag');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', color: '#3B82F6', is_featured: false });
    setEditingTag(null);
  };

  const createSampleTags = async () => {
    const sampleTags = [
      { name: 'Technology', description: 'Technology integration and digital tools', color: '#3B82F6', is_featured: true },
      { name: 'Leadership', description: 'Leadership development and management', color: '#8B5CF6', is_featured: true },
      { name: 'New Teacher Program', description: 'Support and training for new educators', color: '#10B981', is_featured: true },
      { name: 'Required Training', description: 'Mandatory professional development', color: '#EF4444', is_featured: false },
      { name: 'Professional Skills', description: 'General professional development', color: '#F59E0B', is_featured: false },
      { name: 'Safety & Compliance', description: 'Safety protocols and compliance training', color: '#F97316', is_featured: false }
    ];

    try {
      const { error } = await supabase
        .from('course_tags')
        .insert(sampleTags);

      if (error) throw error;
      toast.success('Sample tags created successfully');
      fetchTags();
    } catch (error) {
      console.error('Error creating sample tags:', error);
      toast.error('Failed to create sample tags');
    }
  };

  if (!user) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="p-6 text-center">
            <h2 className="text-xl font-semibold mb-2">Please Log In</h2>
            <p className="text-muted-foreground">You need to be logged in to manage tags.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">Loading tags...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Tag className="h-8 w-8 text-primary" />
            Tag Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Create and manage tags to categorize courses in the catalog{!isAdmin && ' (Only admins can delete tags)'}
          </p>
        </div>
        <div className="flex gap-2">
          {tags.length === 0 && (
            <Button variant="outline" onClick={createSampleTags}>
              <Plus className="h-4 w-4 mr-2" />
              Create Sample Tags
            </Button>
          )}
          <Dialog open={showCreateDialog} onOpenChange={(open) => {
            setShowCreateDialog(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Create Tag
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{editingTag ? 'Edit Tag' : 'Create New Tag'}</DialogTitle>
                <DialogDescription>
                  Tags help organize and filter courses in the catalog.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Technology, Leadership"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Brief description of this tag category"
                    rows={2}
                  />
                </div>
                <div>
                  <Label htmlFor="color">Color</Label>
                  <div className="flex gap-2 mt-2">
                    {PRESET_COLORS.map((color) => (
                      <button
                        key={color}
                        type="button"
                        className={`w-8 h-8 rounded-full border-2 ${
                          formData.color === color ? 'border-foreground' : 'border-muted'
                        }`}
                        style={{ backgroundColor: color }}
                        onClick={() => setFormData(prev => ({ ...prev, color }))}
                      />
                    ))}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="featured"
                    checked={formData.is_featured}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_featured: checked }))}
                  />
                  <Label htmlFor="featured" className="flex items-center gap-1">
                    Featured Tag <Star className="h-3 w-3" />
                  </Label>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowCreateDialog(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit">
                    {editingTag ? 'Update Tag' : 'Create Tag'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {tags.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Tag className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Tags Created</h3>
            <p className="text-muted-foreground mb-4">
              Create tags to help organize and filter courses in the catalog.
            </p>
            <Button onClick={createSampleTags}>
              <Plus className="h-4 w-4 mr-2" />
              Create Sample Tags
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {tags.map((tag) => (
            <Card key={tag.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge 
                      style={{ 
                        backgroundColor: tag.color || '#3B82F6',
                        color: 'white' 
                      }}
                      className="border-0"
                    >
                      {tag.name}
                      {tag.is_featured && <Star className="h-3 w-3 ml-1" />}
                    </Badge>
                  </div>
                  <div className="flex gap-1">
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => handleEdit(tag)}
                    >
                      <Edit className="h-3 w-3" />
                    </Button>
                    {isAdmin && (
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => handleDelete(tag.id)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </div>
                {tag.description && (
                  <p className="text-sm text-muted-foreground mt-2">
                    {tag.description}
                  </p>
                )}
              </CardHeader>
            </Card>
          ))}
        </div>
      )}

      {tags.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="h-5 w-5" />
              How to Use Tags
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div>
                <h4 className="font-medium mb-2">Assigning Tags to Courses</h4>
                <ul className="space-y-1 text-muted-foreground">
                  <li>• Edit any course in the Course Builder</li>
                  <li>• Select tags from the available options</li>
                  <li>• Multiple tags can be assigned per course</li>
                  <li>• Tags appear on course cards in the catalog</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium mb-2">Filtering with Tags</h4>
                <ul className="space-y-1 text-muted-foreground">
                  <li>• Users can filter courses by clicking tags</li>
                  <li>• Featured tags appear prominently with star icons</li>
                  <li>• Multiple tag selection shows courses with any selected tag</li>
                  <li>• Tags use custom colors for visual organization</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}